import { useCallback, useEffect, useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getMessage } from '@gef-ui/localization/messages';
import ArrowRight from '@gef-ui/material-icons/ArrowRight';

import { ComponentLoader } from 'components';
import fetch from 'features/common/fetch';
import { gtmDashboardSectionNames } from 'features/views/dashboard/constants';
import { useDetailButtonClickDataLayer, usePlusMoreButtonClickDataLayer } from 'features/views/dashboard/hooks';
import { fetchAvailableLeads } from 'features/views/dashboard/marketPlace/actions';
import { getAvailableLeadsSplitted } from 'features/views/dashboard/marketPlace/selectors';
import Lead from 'pages/MarketPlace/components/Lead';

import PlusMoreButton from './PlusMoreButton';

export const AvailableLeads = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const pushToClickDetailButtonDataLayer = useDetailButtonClickDataLayer();
	const pushToClickPlusMoreButtonDataLayer = usePlusMoreButtonClickDataLayer();

	const { leadsToShow, originalLength } = useSelector(getAvailableLeadsSplitted);
	const plusMoreValue = originalLength - 3;

	useEffect(() => {
		dispatch(fetchAvailableLeads());
	}, [dispatch]);

	const handleRedirect = useCallback(() => {
		navigate('/market-place');
	}, [navigate]);

	const handleRedirectDetailButton = useCallback(() => {
		pushToClickDetailButtonDataLayer({
			value: gtmDashboardSectionNames.AVAILABLE_LEADS,
		})();
		handleRedirect();
	}, [pushToClickDetailButtonDataLayer, handleRedirect]);

	const handleRedirectPlusMoreButton = useCallback(() => {
		pushToClickPlusMoreButtonDataLayer({
			value: gtmDashboardSectionNames.AVAILABLE_LEADS,
		})();
		handleRedirect();
	}, [pushToClickPlusMoreButtonDataLayer, handleRedirect]);

	const noResults = useMemo(
		() => (
			<Grid size={12}>
				<Alert severity="info">{getMessage('dashboard:marketPlace.noAvailableLeadsToTake')}</Alert>
			</Grid>
		),
		[],
	);

	return (
		<Stack spacing={1}>
			<Stack
				direction="row"
				sx={{
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
			>
				<Typography sx={{ fontWeight: 'bold' }} variant="body1">
					{getMessage('dashboard:marketPlace.availableLeadsTitle')}
				</Typography>
				<Button
					sx={{ width: 'max-content', justifyContent: 'left' }}
					variant="text"
					endIcon={<ArrowRight />}
					onClick={handleRedirectDetailButton}
				>
					{getMessage('dashboard:detail')}
				</Button>
			</Stack>
			<ComponentLoader fetchStorePath={fetch.constants.urls.availableLeads} forceInlineError>
				<Paper sx={{ p: 3, borderRadius: 1, backgroundColor: (theme) => theme.palette.background[100] }}>
					<Stack spacing={2}>
						<Grid container>
							<Grid size={12}>
								<Grid container spacing={2}>
									{isNilOrEmpty(leadsToShow)
										? noResults
										: leadsToShow.map((lead) => (
												<Grid size={{ xs: 12, lg: 4 }}>
													<Lead lead={lead} />
												</Grid>
										  ))}
								</Grid>
							</Grid>
						</Grid>
						<PlusMoreButton value={plusMoreValue} onClick={handleRedirectPlusMoreButton} />
					</Stack>
				</Paper>
			</ComponentLoader>
		</Stack>
	);
};

export default AvailableLeads;
